import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Booking } from "@/lib/api";
import { Check, Clock, X } from "lucide-react";

export default function BookSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("checking");
    const [data, setData] = useState(null);
    const pollingRef = useRef({ attempts: 0, stop: false });

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        const poll = async () => {
            if (pollingRef.current.stop) return;
            if (pollingRef.current.attempts >= 8) {
                setStatus("timeout");
                return;
            }
            pollingRef.current.attempts += 1;
            try {
                const res = await Booking.status(sessionId);
                setData(res);
                if (res.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (res.status === "expired") {
                    setStatus("expired");
                    return;
                }
                setTimeout(poll, 2000);
            } catch {
                setTimeout(poll, 2000);
            }
        };
        poll();
        return () => {
            pollingRef.current.stop = true;
        };
    }, [sessionId]);

    return (
        <div className="min-h-screen" data-testid="book-success-page">
            <Header />
            <section className="max-w-3xl mx-auto px-6 md:px-10 pt-20 pb-24">
                <div className="panel p-10 md:p-14 text-center">
                    {status === "checking" && (
                        <div data-testid="status-checking">
                            <Clock size={32} className="mx-auto mb-6" style={{ color: "var(--cobalt)" }} />
                            <div className="overline-cobalt mb-4">[Status · Verifying]</div>
                            <h1
                                className="font-display"
                                style={{ fontSize: "clamp(32px, 4vw, 52px)", color: "var(--ink)" }}
                            >
                                Confirming{" "}
                                <span
                                    style={{
                                        fontFamily: "'Fraunces', serif",
                                        fontStyle: "italic",
                                        fontWeight: 400,
                                        color: "var(--cobalt)",
                                    }}
                                >
                                    payment
                                </span>
                                …
                            </h1>
                            <p className="mt-4" style={{ color: "var(--ink-2)" }}>
                                This usually takes a few seconds.
                            </p>
                        </div>
                    )}

                    {status === "paid" && (
                        <div data-testid="status-paid">
                            <div
                                className="inline-flex w-16 h-16 items-center justify-center border mb-7"
                                style={{
                                    borderColor: "var(--cobalt)",
                                    color: "var(--cobalt)",
                                }}
                            >
                                <Check size={24} />
                            </div>
                            <div className="overline-cobalt mb-4">[Status · Confirmed]</div>
                            <h1
                                className="font-display"
                                style={{
                                    fontSize: "clamp(36px, 5vw, 64px)",
                                    lineHeight: 0.98,
                                    color: "var(--ink)",
                                }}
                            >
                                Booked. See you at the{" "}
                                <span
                                    style={{
                                        fontFamily: "'Fraunces', serif",
                                        fontStyle: "italic",
                                        fontWeight: 400,
                                        color: "var(--cobalt)",
                                    }}
                                >
                                    frontier.
                                </span>
                            </h1>
                            <p className="mt-6 max-w-xl mx-auto" style={{ color: "var(--ink-2)" }}>
                                A confirmation email is on its way to{" "}
                                <span style={{ color: "var(--cobalt)" }}>{data?.booking?.email}</span>.
                                Calendar invite follows shortly.
                            </p>
                            <Link to="/" className="btn-ghost mt-8" data-testid="back-home">
                                Back to Frontier
                            </Link>
                        </div>
                    )}

                    {(status === "expired" || status === "timeout" || status === "error") && (
                        <div data-testid={`status-${status}`}>
                            <div
                                className="inline-flex w-16 h-16 items-center justify-center border mb-7"
                                style={{
                                    borderColor: "var(--orange)",
                                    color: "var(--orange)",
                                }}
                            >
                                <X size={24} />
                            </div>
                            <h1 className="font-display" style={{ fontSize: "clamp(32px, 4vw, 52px)" }}>
                                Payment not confirmed.
                            </h1>
                            <p className="mt-4" style={{ color: "var(--ink-2)" }}>
                                If you completed payment, it may take a moment to
                                settle. Otherwise, try again.
                            </p>
                            <Link to="/book" className="btn-primary mt-8">
                                Try again
                            </Link>
                        </div>
                    )}
                </div>
            </section>
            <Footer />
        </div>
    );
}
